import "./CartSubtitle.css";
import React, { useState, useRef, useContext } from "react";
import { motion } from "framer-motion";
import {
  FinancialData,
  ItemDetailsData,
  RecommendedItem,
} from "../../../../Utils/DefinitionsInterfaces";
import Cart from "../Cart/Cart";
import FormCluster from "../FormCluster/FormCluster";
import FormCluster2 from "../FormCluster/FormCluster2";
import ItemInfoModal from "../ItemInfoModal/ItemInfoModal";
import { doNFOTransactionResponse } from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import usePricing from "../../../../Utils/hooks/usePricing";
import PricingService from "../../../../Utils/PricingService";
import { CartContext } from "../../../../App";

interface CartSubtitleProps {
  stateDefaultItems: FinancialData[];
  stateCheckoutItems: ItemDetailsData[];
  stateRecommendedItems: RecommendedItem[];
  cbRemoveProduct: (itemId: number) => void;
  cbAddRecommended: (itemId: number) => void;
  cbHandlePaymentClick: (data?: doNFOTransactionResponse) => void;
  // Coupon props
  couponState: {
    code: string;
    isApplied: boolean;
    isLoading: boolean;
    discountAmount: number;
    originalAmount: number;
    finalAmount: number;
    error: string;
  };
  couponInput: string;
  setCouponInput: (value: string) => void;
  handleApplyCoupon: (codeToApply?: string) => void;
  handleRemoveCoupon: () => void;
  isCouponEnabled: () => boolean;
}

const CartSubtitle = (props: CartSubtitleProps) => {
  const formCluster2Ref = useRef<HTMLDivElement>(null);
  const [itemInfoVisible, setItemInfoVisible] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);
  const [triviaModalVisible, setTriviaModalVisible] = useState(false);
  const { isBundlePricingActive, formatPrice } = usePricing("cod");
  const { bundleInfo, checkOutItems } = useContext(CartContext);

  // // Bundle pricing header component - memoized to prevent unnecessary re-renders
  // const BundlePricingHeader = useMemo(() => {
  //   if (props.stateRecommendedItems.length === 0) {
  //     return null;
  //   }

  //   // Get the lowest selling price to show in header
  //   const lowestSellingPrice = Math.min(
  //     ...props.stateRecommendedItems
  //       .filter((item) => item.id !== 3)
  //       .map((item) => item.selling_price)
  //   );

  //   // Get the total price (sum of all selling prices)
  //   const totalPrice = props.stateRecommendedItems
  //     .filter((item) => item.id !== 3)
  //     .reduce((total, item) => item.selling_price + item.selling_price, 0);

  //   return (
  //     <motion.div
  //       initial={{ opacity: 0, y: 10 }}
  //       animate={{ opacity: 1, y: 0 }}
  //       transition={{ duration: 0.3 }}
  //       style={{
  //         backgroundColor: "rgba(36, 108, 193, 0.05)",
  //         border: "1px solid rgba(36, 108, 193, 0.15)",
  //         borderTopLeftRadius: "12px",
  //         borderTopRightRadius: "12px",
  //         padding: "12px 16px",
  //         display: "flex",
  //         alignItems: "center",
  //         justifyContent: "center",
  //         width: "90%",
  //       }}
  //     >
  //       <div
  //         style={{
  //           color: "rgba(36, 108, 193, 1)",
  //           fontSize: "16px",
  //           fontWeight: "500",
  //           textAlign: "center",
  //           display: "flex",
  //           alignItems: "center",
  //           justifyContent: "space-between",
  //           gap: "8px",
  //           width: "100%",
  //         }}
  //       >
  //         <div
  //           style={{
  //             backgroundColor: "rgba(36, 108, 193, 0.1)",
  //             padding: "4px 8px",
  //             borderRadius: "6px",
  //             fontSize: "12px",
  //             fontWeight: "600",
  //           }}
  //         >
  //           🎉 Bundle Deal
  //         </div>
  //         <div
  //           style={{
  //             fontSize: "14px",
  //             fontWeight: "600",
  //           }}
  //         >
  //           Special price of ₹{totalPrice} for 2 books (₹
  //           {lowestSellingPrice} for 2nd book)
  //         </div>
  //       </div>
  //     </motion.div>
  //   );
  // }, [props.stateRecommendedItems, isBundlePricingActive()]);

  // // Helper function to get display price for recommended items
  // const getRecommendedItemPrice = (item: RecommendedItem) => {
  //   return item.selling_price;
  // };

  // Helper function to get discount percentage for recommended items
  const getDiscountPercentage = (item: RecommendedItem) => {
    if (item.id === 3) {
      return Math.round(
        ((item.base_price - item.selling_price) / item.base_price) * 100
      );
    }

    // For Grade 6-8 and 10-12 items (1 and 2), check if bundle discount applies
    const isPfhItem = item.id === 1 || item.id === 2;
    if (isPfhItem) {
      // Check if the other PFH item is in cart
      const otherPfhItemId = item.id === 1 ? 2 : 1;
      const hasOtherPfhItem = checkOutItems.some(
        (cartItem) => cartItem.id === otherPfhItemId
      );

      // If the other item is in cart, use discountedBundlePrice for calculation
      if (hasOtherPfhItem && item.discountedBundlePrice > 0) {
        return Math.round(
          ((item.base_price - item.discountedBundlePrice) / item.base_price) *
            100
        );
      }
    }

    // For Grade 3-4 items (10 and 13), check if bundle discount applies
    const isGrade34Item = item.id === 10 || item.id === 13;
    if (isGrade34Item) {
      // Check if the other Grade 3-4 item is in cart
      const otherGrade34ItemId = item.id === 10 ? 13 : 10;
      const hasOtherGrade34Item = checkOutItems.some(
        (cartItem) => cartItem.id === otherGrade34ItemId
      );

      // If the other item is in cart, use discountedBundlePrice for calculation
      if (hasOtherGrade34Item && item.discountedBundlePrice > 0) {
        return Math.round(
          ((item.base_price - item.discountedBundlePrice) / item.base_price) *
            100
        );
      }
    }

    return PricingService.getRecommendedItemDiscountPercentage(item);
  };

  // Helper function to check if bundle messaging should be shown
  const shouldShowBundleMessage = (
    item: RecommendedItem
  ): {
    show: boolean;
    isBundleActive: boolean;
  } => {
    // For Grade 6-8 and 10-12 items (1 and 2)
    const isPfhItem = item.id === 1 || item.id === 2;
    if (isPfhItem) {
      // Check if the other PFH item is in cart
      const otherPfhItemId = item.id === 1 ? 2 : 1;
      const hasOtherPfhItem = checkOutItems.some(
        (cartItem) => cartItem.id === otherPfhItemId
      );

      return {
        show: true,
        isBundleActive: hasOtherPfhItem && item.discountedBundlePrice > 0,
      };
    }

    // For Grade 3-4 items (10 and 13)
    const isGrade34Item = item.id === 10 || item.id === 13;
    if (isGrade34Item) {
      // Check if the other Grade 3-4 item is in cart
      const otherGrade34ItemId = item.id === 10 ? 13 : 10;
      const hasOtherGrade34Item = checkOutItems.some(
        (cartItem) => cartItem.id === otherGrade34ItemId
      );

      return {
        show: true,
        isBundleActive: hasOtherGrade34Item && item.discountedBundlePrice > 0,
      };
    }

    return { show: false, isBundleActive: false };
  };

  // Helper function to get display price for recommended items
  // Uses discountedBundlePrice when bundle discount applies
  // Exception: items with id === 3 always use selling_price
  const getRecommendedItemDisplayPrice = (item: RecommendedItem): number => {
    // Exception: items with id === 3 always use selling_price
    if (item.id === 3) {
      return item.selling_price;
    }

    // For Grade 6-8 and 10-12 items (1 and 2), check if bundle discount applies
    const isPfhItem = item.id === 1 || item.id === 2;
    if (isPfhItem) {
      // Check if the other PFH item is in cart
      const otherPfhItemId = item.id === 1 ? 2 : 1;
      const hasOtherPfhItem = checkOutItems.some(
        (cartItem) => cartItem.id === otherPfhItemId
      );

      // If the other item is in cart, use discountedBundlePrice
      if (hasOtherPfhItem && item.discountedBundlePrice > 0) {
        return item.discountedBundlePrice;
      }
    }

    // For Grade 3-4 items (10 and 13), check if bundle discount applies
    const isGrade34Item = item.id === 10 || item.id === 13;
    if (isGrade34Item) {
      // Check if the other Grade 3-4 item is in cart
      const otherGrade34ItemId = item.id === 10 ? 13 : 10;
      const hasOtherGrade34Item = checkOutItems.some(
        (cartItem) => cartItem.id === otherGrade34ItemId
      );

      // If the other item is in cart, use discountedBundlePrice
      if (hasOtherGrade34Item && item.discountedBundlePrice > 0) {
        return item.discountedBundlePrice;
      }
    }

    // Identify the primary/default item
    const primaryItemId =
      bundleInfo.primaryProductId ??
      (checkOutItems.length > 0 ? checkOutItems[0].id : null);

    const isPrimaryItem = primaryItemId !== null && item.id === primaryItemId;

    // For the primary item, use selling_price
    // For items different from the primary item, use discountedBundlePrice
    if (isPrimaryItem) {
      return item.selling_price;
    } else {
      // Use discountedBundlePrice if available, otherwise fall back to selling_price
      return item.discountedBundlePrice > 0
        ? item.discountedBundlePrice
        : item.selling_price;
    }
  };

  const handleShowItemInfo = (index: number) => {
    setCurrentItemIndex(index);
    setItemInfoVisible(true);
    sessionStorage.setItem("currentItemIndex", index.toString());
  };

  const handleCloseItemInfo = () => {
    setItemInfoVisible(false);
    sessionStorage.removeItem("currentItemIndex");
  };

  const handleOpenTriviaModal = () => {
    setTriviaModalVisible(true);
  };

  const handleCloseTriviaModal = () => {
    setTriviaModalVisible(false);
  };

  const handleRecommendedClick = (itemId: number) => {
    props.cbAddRecommended(itemId);
    // if (itemId === 3) {
    //   formCluster2Ref.current?.scrollIntoView({
    //     behavior: "smooth",
    //     block: "start",
    //   });
    // }
  };

  // Remove the useEffect since pricing is now handled by the pricing service

  return (
    <div className="cart-parent-wrapper">
      <div className="recommended-items-mobile">
        {/* <TriviaBanner onClick={handleOpenTriviaModal} /> */}
        {/* {BundlePricingHeader} */}
        {props.stateRecommendedItems.map((e, i) => {
          return (
            <motion.div
              key={`recommended-item-${i}`}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.01 }}
              style={{
                backgroundColor: "rgba(255, 255, 255, 1)",
                border: "solid 1px rgba(0,0,0,0.1)",
                width: "100%",
                padding: "10px 10px",
                borderRadius: "20px",
                marginBottom: "15px",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    color: "rgba(36, 108, 193, 1)",
                    alignSelf: "center",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    fontSize: "12px",
                    backgroundColor: "rgba(36, 108, 193, 0.1)",
                    width: "fit-content",
                    padding: "6px 6px",
                    borderRadius: "8px",
                  }}
                >
                  {e.id === 3 ? "Parent's Favorite" : "Recommended Items"}
                </div>
                {e.id === 3 && (
                  <div
                    onClick={() => handleShowItemInfo(i)}
                    style={{
                      backgroundColor: "rgba(36, 108, 193, 1)",
                      minWidth: "22px",
                      minHeight: "22px",
                      maxWidth: "22px",
                      maxHeight: "22px",
                      marginLeft: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "100px",
                    }}
                  >
                    <span style={{ color: "white", fontSize: "12px" }}>i</span>
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                  alignItems: "flex-end",
                }}
              >
                <div
                  className="text-wrapper-3"
                  style={{
                    maxWidth: "250px",
                    color: "rgba(36, 108, 193, 1)",
                    alignSelf: "center",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <img
                    src={e.image}
                    alt={e.name}
                    style={{ width: "50px", height: "50px" }}
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      fontSize: "14px",
                    }}
                  >
                    {e.name}
                    <span
                      style={{
                        marginTop: "5px",
                        backgroundColor: "rgba(2, 59, 143, 0.1)",
                        color: "rgba(2, 59, 143, 1)",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "500",
                        width: "fit-content",
                      }}
                    >
                      {e.id === 3
                        ? "100% online exam"
                        : `${getDiscountPercentage(e)}% OFF`}
                    </span>
                    {(() => {
                      const bundleMessage = shouldShowBundleMessage(e);
                      if (bundleMessage.show) {
                        return bundleMessage.isBundleActive ? (
                          <span
                            style={{
                              marginTop: "5px",
                              backgroundColor: "rgba(19, 136, 8, 0.1)",
                              color: "rgba(19, 136, 8, 1)",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                              width: "fit-content",
                            }}
                          >
                            Additional 10% OFF
                          </span>
                        ) : (
                          <div
                            style={{
                              marginTop: "5px",
                              color: "rgba(36, 108, 193, 0.8)",
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "1.4",
                            }}
                          >
                            Add another book to get additional Rs.100 Off
                            (Additional 10% OFF)
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {/* {e.id === 3 && (
                      <div
                        style={{
                          color: "rgba(36, 108, 193, 0.8)",
                          fontSize: "12px",
                          fontWeight: "400",
                          fontFamily: "OggTextMedium",
                          marginTop: "5px",
                        }}
                      >
                        Exam Scheduled on : <br />
                        {e.examDate && `${formatExamDate(e.examDate)}`}
                      </div>
                    )} */}
                  </div>
                </div>

                <div
                  onClick={() => {
                    handleRecommendedClick(e.id);
                  }}
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <span
                      style={{
                        textDecoration: "line-through",
                        marginRight: "5px",
                        color: "rgba(138, 138, 138, 0.8)",
                        fontSize: "12px",
                      }}
                      className="text-wrapper-3"
                    >
                      {"₹" + e.base_price}
                    </span>
                    <span
                      style={{ fontSize: "18px" }}
                      className="text-wrapper-3"
                    >
                      {"₹" + getRecommendedItemDisplayPrice(e)}
                    </span>
                  </div>
                  <div
                    style={{
                      backgroundColor: " rgba(138, 138, 138, 0.1)",
                      border: "0.56px solid rgba(138, 138, 138, 0.1)",
                      padding: "8px",
                      marginTop: "10px",
                      color: "rgba(2, 59, 143, 1)",
                      alignItems: "center",
                      justifyContent: "center",
                      display: "flex",
                      cursor: "pointer",
                      borderRadius: "10px",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add to cart now
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="cart-subtitle">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FormCluster2 />
          <div className="delivery-address-title">
            <div className="delivery-address-title-text">Delivery Address</div>
            <div className="line-divider"></div>
          </div>

          <FormCluster />
        </div>
        <div className="outer-container">
          <div className="inner-divider"></div>
        </div>
        <div className="recommended-items-container">
          <div className="recommended-items">
            {/* <TriviaBanner onClick={handleOpenTriviaModal} /> */}
            {/* {BundlePricingHeader} */}
            {props.stateRecommendedItems.map((e, i) => {
              return (
                <motion.div
                  key={`recommended-item-${i}`}
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                  whileHover={{ scale: 1.01 }}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    border: "solid 1px rgba(0,0,0,0.1)",
                    width: "100%",
                    padding: "20px 25px",
                    borderRadius: "20px",
                    marginBottom: "15px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        color: "rgba(36, 108, 193, 1)",
                        alignSelf: "center",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        fontSize: "18px",

                        backgroundColor: "rgba(36, 108, 193, 0.1)",
                        width: "fit-content",
                        padding: "10px 10px",
                        borderRadius: "10px",
                      }}
                    >
                      {e.id === 3 ? "Parent's Favorite" : "Recommended Items"}
                    </div>
                    {e.id === 3 && (
                      <div
                        onClick={() => handleShowItemInfo(i)}
                        style={{
                          backgroundColor: "rgba(36, 108, 193, 1)",
                          minWidth: "30px",
                          minHeight: "30px",
                          maxWidth: "30px",
                          maxHeight: "30px",
                          marginLeft: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "100px",
                        }}
                      >
                        <span style={{ color: "white", fontSize: "20px" }}>
                          i
                        </span>
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      className="text-wrapper-3"
                      style={{
                        maxWidth: "250px",
                        color: "rgba(36, 108, 193, 1)",
                        alignSelf: "center",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <img
                        src={e.image}
                        alt={e.name}
                        style={{ width: "80px", height: "80px" }}
                      />
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {e.name}
                        <span
                          style={{
                            marginTop: "5px",
                            backgroundColor: "rgba(2, 59, 143, 0.1)",
                            color: "rgba(2, 59, 143, 1)",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontWeight: "500",
                            width: "fit-content",
                          }}
                        >
                          {e.id === 3
                            ? "100% online exam"
                            : `${getDiscountPercentage(e)}% OFF`}
                        </span>
                        {(() => {
                          const bundleMessage = shouldShowBundleMessage(e);
                          if (bundleMessage.show) {
                            return bundleMessage.isBundleActive ? (
                              <span
                                style={{
                                  marginTop: "5px",
                                  backgroundColor: "rgba(19, 136, 8, 0.1)",
                                  color: "rgba(19, 136, 8, 1)",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  width: "fit-content",
                                }}
                              >
                                Additional 10% OFF
                              </span>
                            ) : (
                              <div
                                style={{
                                  marginTop: "5px",
                                  color: "rgba(36, 108, 193, 0.8)",
                                  fontSize: "12px",
                                  fontWeight: "400",
                                  lineHeight: "1.4",
                                }}
                              >
                                Add another book to get additional Rs.100 Off
                                (Additional 10% OFF)
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {/* {e.id === 3 && (
                          <div
                            style={{
                              color: "rgba(36, 108, 193, 0.8)",
                              fontSize: "14px",
                              fontWeight: "400",
                              fontFamily: "OggTextMedium",
                              marginTop: "5px",
                            }}
                          >
                            Exam Scheduled on : <br />
                            {e.examDate && `${formatExamDate(e.examDate)}`}
                          </div>
                        )} */}
                      </div>
                    </div>

                    <div
                      onClick={() => {
                        handleRecommendedClick(e.id);
                      }}
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            textDecoration: "line-through",
                            marginRight: "10px",
                            color: "rgba(138, 138, 138, 0.8)",
                          }}
                          className="text-wrapper-3"
                        >
                          {"₹" + e.base_price}
                        </span>
                        <span className="text-wrapper-3">
                          {"₹" + getRecommendedItemDisplayPrice(e)}
                        </span>
                      </div>
                      <div
                        style={{
                          backgroundColor: " rgba(138, 138, 138, 0.1)",
                          border: "0.56px solid rgba(138, 138, 138, 0.1)",
                          padding: "8px",
                          marginTop: "10px",
                          color: "rgba(2, 59, 143, 1)",
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                          cursor: "pointer",
                          borderRadius: "10px",
                        }}
                      >
                        Add to cart now
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <Cart
            stateDefaultItems={props.stateDefaultItems}
            stateCheckoutItems={props.stateCheckoutItems}
            stateRecommendedItems={props.stateRecommendedItems}
            cbRemoveProduct={props.cbRemoveProduct}
            cbAddRecommended={props.cbAddRecommended}
            cbHandlePaymentClick={props.cbHandlePaymentClick}
            couponState={props.couponState}
            couponInput={props.couponInput}
            setCouponInput={props.setCouponInput}
            handleApplyCoupon={props.handleApplyCoupon}
            handleRemoveCoupon={props.handleRemoveCoupon}
            isCouponEnabled={props.isCouponEnabled}
          />
        </div>
      </div>

      <ItemInfoModal
        examDate={props.stateRecommendedItems[currentItemIndex]?.examDate || ""}
        image={props.stateRecommendedItems[currentItemIndex]?.image || ""}
        title={props.stateRecommendedItems[currentItemIndex]?.name || ""}
        description={""}
        isVisible={itemInfoVisible}
        currentItemId={props.stateRecommendedItems[currentItemIndex]?.id || 0}
        onClose={handleCloseItemInfo}
        cbHandleCloseItemInfo={handleCloseItemInfo}
        cbAddRecommended={props.cbAddRecommended}
      >
        <div></div>
      </ItemInfoModal>

      {/* <TriviaModal
        isVisible={triviaModalVisible}
        onClose={handleCloseTriviaModal}
      /> */}
    </div>
  );
};

export default CartSubtitle;
